import { useEffect, useState, useCallback } from "react";
import Layout from "../components/Layout";
import PageAdvice from "../components/PageAdvice";
import TimesheetCharts from "../components/TimesheetCharts";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  Clock, MagnifyingGlass, DownloadSimple, CircleNotch, CurrencyDollar,
  Briefcase, Buildings, User, FunnelSimple, X,
} from "@phosphor-icons/react";

// Quick date-range presets (returns {start, end} as YYYY-MM-DD).
const pad = (n) => String(n).padStart(2, "0");
const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const RANGES = {
  this_month: () => { const n = new Date(); return { start: iso(new Date(n.getFullYear(), n.getMonth(), 1)), end: iso(new Date(n.getFullYear(), n.getMonth() + 1, 0)) }; },
  last_month: () => { const n = new Date(); return { start: iso(new Date(n.getFullYear(), n.getMonth() - 1, 1)), end: iso(new Date(n.getFullYear(), n.getMonth(), 0)) }; },
  last_7: () => { const n = new Date(); const s = new Date(); s.setDate(n.getDate() - 6); return { start: iso(s), end: iso(n) }; },
  last_30: () => { const n = new Date(); const s = new Date(); s.setDate(n.getDate() - 29); return { start: iso(s), end: iso(n) }; },
  this_year: () => { const n = new Date(); return { start: iso(new Date(n.getFullYear(), 0, 1)), end: iso(new Date(n.getFullYear(), 11, 31)) }; },
  last_fy: () => { const n = new Date(); const y = n.getMonth() >= 6 ? n.getFullYear() - 1 : n.getFullYear() - 2; return { start: `${y}-07-01`, end: `${y + 1}-06-30` }; },
  this_fy: () => { const n = new Date(); const y = n.getMonth() >= 6 ? n.getFullYear() : n.getFullYear() - 1; return { start: `${y}-07-01`, end: `${y + 1}-06-30` }; },
};
const RANGE_LABELS = [
  ["this_month", "This month"], ["last_month", "Last month"], ["last_7", "Last 7 days"],
  ["last_30", "Last 30 days"], ["this_fy", "This FY"], ["last_fy", "Last FY"], ["this_year", "This year"],
];
const money = (n) => (n == null ? "—" : `$${Number(n).toLocaleString("en-AU", { maximumFractionDigits: 0 })}`);

export default function Timesheets() {
  const { user } = useAuth();
  const [facets, setFacets] = useState(null);
  const [technician, setTechnician] = useState("");
  const [client, setClient] = useState("");
  const [worktype, setWorktype] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [preset, setPreset] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [appliedFilters, setAppliedFilters] = useState({ technician: "", client: "", worktype: "", start_date: "", end_date: "" });
  const limit = 50;

  useEffect(() => { api.get("/timesheets/facets").then((r) => setFacets(r.data)).catch(() => setFacets({ clients: [], technicians: [], worktypes: [] })); }, []);

  const search = useCallback(async (p = 1) => {
    setLoading(true);
    setAppliedFilters({ technician, client, worktype, start_date: startDate, end_date: endDate });
    try {
      const { data } = await api.get("/timesheets", {
        params: { technician, client, worktype, start_date: startDate, end_date: endDate, page: p, limit },
      });
      setData(data);
      setPage(p);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Search failed");
    } finally {
      setLoading(false);
    }
  }, [technician, client, worktype, startDate, endDate]);

  useEffect(() => { search(1); /* initial */ }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const applyPreset = (key) => {
    setPreset(key);
    if (!key) { setStartDate(""); setEndDate(""); return; }
    const { start, end } = RANGES[key]();
    setStartDate(start); setEndDate(end);
  };

  const clearAll = () => {
    setTechnician(""); setClient(""); setWorktype(""); setStartDate(""); setEndDate(""); setPreset("");
    setTimeout(() => search(1), 0);
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/timesheets/export", {
        params: { technician, client, worktype, start_date: startDate, end_date: endDate },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = "timesheets.csv"; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("CSV exported.");
    } catch {
      toast.error("Export failed.");
    }
  };

  const selectCls = "w-full rounded-sm border border-neutral-300 bg-white px-3 py-2 text-sm outline-none focus:border-[#0055FF]";

  return (
    <Layout>
      <div data-testid="timesheets-page">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="text-sm uppercase tracking-widest font-semibold text-neutral-500">Legacy API · Labour</p>
            <h1 className="mt-1 font-heading text-4xl sm:text-5xl font-black tracking-tighter text-[#0A0A0A]">Timesheets</h1>
          </div>
          <button data-testid="ts-export-btn" onClick={exportCsv}
            className="inline-flex items-center gap-2 rounded-sm bg-[#0A0A0A] px-4 py-2.5 text-sm font-semibold text-white transition-all duration-200 hover:bg-[#0055FF]">
            <DownloadSimple size={16} weight="bold" /> Export CSV
          </button>
        </div>

        {/* Filters */}
        <div className="mt-6 rounded-sm border border-neutral-200 bg-white p-5 shadow-sm" data-testid="ts-filters">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div>
              <label className="mb-1.5 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-neutral-500"><User size={13} /> Technician</label>
              <input data-testid="ts-filter-technician" list="ts-techs" value={technician} onChange={(e) => setTechnician(e.target.value)} placeholder="All technicians" className={selectCls} />
              <datalist id="ts-techs">{facets?.technicians?.map((t) => <option key={t} value={t} />)}</datalist>
            </div>
            <div>
              <label className="mb-1.5 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-neutral-500"><Buildings size={13} /> Client</label>
              <input data-testid="ts-filter-client" list="ts-clients" value={client} onChange={(e) => setClient(e.target.value)} placeholder="All clients" className={selectCls} />
              <datalist id="ts-clients">{facets?.clients?.map((c) => <option key={c} value={c} />)}</datalist>
            </div>
            <div>
              <label className="mb-1.5 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-neutral-500"><FunnelSimple size={13} /> Work type</label>
              <select data-testid="ts-filter-worktype" value={worktype} onChange={(e) => setWorktype(e.target.value)} className={selectCls}>
                <option value="">All work types</option>
                {facets?.worktypes?.map((w) => <option key={w} value={w}>{w}</option>)}
              </select>
            </div>
          </div>

          {/* Quick ranges */}
          <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="ts-quick-ranges">
            {RANGE_LABELS.map(([k, label]) => (
              <button key={k} data-testid={`ts-range-${k}`} onClick={() => applyPreset(preset === k ? "" : k)}
                className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-all ${preset === k ? "bg-[#0055FF] text-white" : "bg-neutral-100 text-neutral-600 hover:bg-neutral-200"}`}>
                {label}
              </button>
            ))}
          </div>

          <div className="mt-4 flex flex-wrap items-end gap-3">
            <div>
              <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-neutral-500">From</label>
              <input data-testid="ts-start-date" type="date" value={startDate} onChange={(e) => { setStartDate(e.target.value); setPreset(""); }} className={selectCls} />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-neutral-500">To</label>
              <input data-testid="ts-end-date" type="date" value={endDate} onChange={(e) => { setEndDate(e.target.value); setPreset(""); }} className={selectCls} />
            </div>
            <button data-testid="ts-search-btn" onClick={() => search(1)} disabled={loading}
              className="inline-flex items-center gap-2 rounded-sm bg-[#0055FF] px-5 py-2 text-sm font-semibold text-white transition-all hover:bg-[#0044CC] disabled:opacity-60">
              {loading ? <CircleNotch size={16} className="animate-spin" /> : <MagnifyingGlass size={16} weight="bold" />} Search
            </button>
            <button data-testid="ts-clear-btn" onClick={clearAll} className="inline-flex items-center gap-1.5 rounded-sm border border-neutral-300 px-3 py-2 text-sm font-semibold text-neutral-600 hover:bg-neutral-50">
              <X size={14} /> Clear
            </button>
          </div>
        </div>

        {/* Totals */}
        {data && (
          <div className="mt-6 grid grid-cols-2 gap-px overflow-hidden rounded-sm border border-neutral-200 bg-neutral-200 sm:grid-cols-4" data-testid="ts-totals">
            {[
              { icon: Briefcase, label: "Entries", value: data.count?.toLocaleString() },
              { icon: Clock, label: "Total hours", value: data.totals?.hours?.toLocaleString() },
              { icon: CurrencyDollar, label: "Total charged", value: money(data.totals?.charge) },
              { icon: CurrencyDollar, label: "Total cost", value: money(data.totals?.cost) },
            ].map((k) => (
              <div key={k.label} className="bg-white p-4">
                <p className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-neutral-400"><k.icon size={13} weight="bold" />{k.label}</p>
                <p className="mt-1 font-heading text-2xl font-black tracking-tighter text-[#0A0A0A]">{k.value ?? "—"}</p>
              </div>
            ))}
          </div>
        )}

        {/* Labour charts */}
        {data && <TimesheetCharts filters={appliedFilters} />}

        {/* Results */}
        <div className="mt-6 overflow-hidden rounded-sm border border-neutral-200 bg-white shadow-sm">
          <div className="max-h-[600px] overflow-auto">
            <table className="w-full border-collapse text-sm" data-testid="ts-results-table">
              <thead className="sticky top-0 bg-neutral-50">
                <tr className="border-b border-neutral-200 text-xs uppercase tracking-wider text-neutral-500">
                  {["Work date", "Technician", "Client", "Job #", "Task", "Work type", "Hours", "Charge", "Type"].map((h) => <th key={h} className="px-4 py-2.5 text-left">{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={9} className="py-16 text-center"><CircleNotch size={24} className="mx-auto animate-spin text-[#0055FF]" /></td></tr>
                ) : !data?.entries?.length ? (
                  <tr><td colSpan={9} className="py-16 text-center text-sm text-neutral-400" data-testid="ts-no-results">No timesheets match these filters.</td></tr>
                ) : data.entries.map((e, i) => (
                  <tr key={e.timesheetid || i} className="border-b border-neutral-100 even:bg-neutral-50/60" data-testid={`ts-result-row-${i}`}>
                    <td className="whitespace-nowrap px-4 py-2.5 font-mono text-neutral-700">{e.workdate}</td>
                    <td className="whitespace-nowrap px-4 py-2.5 font-semibold text-[#0A0A0A]">{e.given} {e.surname}</td>
                    <td className="max-w-[200px] truncate px-4 py-2.5 text-neutral-600" title={e.client_name}>{e.client_name || "—"}</td>
                    <td className="px-4 py-2.5 font-mono text-[#0055FF]">{e.jobnumber || "—"}</td>
                    <td className="max-w-[240px] truncate px-4 py-2.5 text-neutral-600" title={e.taskname}>{e.taskname || "—"}</td>
                    <td className="px-4 py-2.5 text-neutral-600">{e.worktype || "—"}</td>
                    <td className="px-4 py-2.5 text-right font-mono">{e.hours}</td>
                    <td className="px-4 py-2.5 text-right font-mono">{money(e.charge)}</td>
                    <td className="px-4 py-2.5"><span className={`rounded-sm px-2 py-0.5 text-xs font-semibold ${e.type === "Productive" ? "bg-[#059669]/10 text-[#059669]" : "bg-neutral-100 text-neutral-600"}`}>{e.type || "—"}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {data && data.pages > 1 && (
            <div className="flex items-center justify-between border-t border-neutral-200 px-4 py-3 text-sm" data-testid="ts-pagination">
              <span className="text-neutral-500">Page {data.page} of {data.pages}</span>
              <div className="flex gap-2">
                <button disabled={page <= 1} onClick={() => search(page - 1)} className="rounded-sm border border-neutral-300 px-3 py-1.5 font-semibold disabled:opacity-40">Prev</button>
                <button disabled={page >= data.pages} onClick={() => search(page + 1)} className="rounded-sm border border-neutral-300 px-3 py-1.5 font-semibold disabled:opacity-40">Next</button>
              </div>
            </div>
          )}
        </div>

        <PageAdvice items={[
          "Timesheets are pulled from the Legacy Aroflo API. Run \u201cSync timesheets\u201d on the Technicians page to refresh the data.",
          "Client is resolved from the job\u2019s linked task — entries on jobs without a matched client show \u201c—\u201d.",
          "Use the quick ranges (This month, Last FY, etc.) or pick exact From/To dates, then Export CSV for the filtered set.",
        ]} />
      </div>
    </Layout>
  );
}
